import React, { useEffect, useRef, useState } from "react";

import {
  ImportPreviousConversationButton,
  MovementTypeStep,
  PlayerSelectStep,
  RebootLiveStepper,
  ReportTypeStep,
  SessionSelectStep,
  TestingPanel,
} from "../../..";
import { SpinnerButton } from "../../../../components";
import {
  AiUploadDataRequest,
  AiUploadDataResponse,
  useUploadAiDataMutation,
} from "../../../../services";
import { useInitContainer, usePlayerSessions } from "../../hooks";
import { FailedSession } from "./ReportTypeStep";

interface Dataset {
  players: string[];
  movementTypeId: string;
  files: File[];
  sessions: string[];
  selectedSessionDate?: string;
}

interface ChatSetupProps {
  dataset: Dataset;
  setDataset: React.Dispatch<React.SetStateAction<Dataset>>;
  inputValue: string;
  setInputValue: (value: string) => void;
  onVoiceTranscript: (transcript: string) => void;
  onInitiateChatWithData: (
    sessionId: string,
    containerId: string,
    userMessageText: string,
    fileIds?: string[],
    dataSummary?: string,
  ) => void;
  onOpenImportModal?: () => void;
}

export const ChatSetup: React.FC<ChatSetupProps> = ({
  dataset,
  setDataset,
  inputValue,
  setInputValue,
  onVoiceTranscript,
  onInitiateChatWithData,
  onOpenImportModal,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingStatus, setLoadingStatus] = useState<
    "loading" | "success" | "error" | null
  >(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [failedUploads, setFailedUploads] = useState<FailedSession[]>([]);
  const [failedSessions, setFailedSessions] = useState<FailedSession[]>([]);
  const [succeededSessions, setSucceededSessions] = useState<string[]>([]);
  const [step1Error, setStep1Error] = useState<string | null>(null);
  const [step1ErrorAllowRetry, setStep1ErrorAllowRetry] = useState(true);
  // Store upload_data response including containerId
  const [uploadDataResponse, setUploadDataResponse] =
    useState<AiUploadDataResponse | null>(null);
  // Generate a random session_id for the chat
  const [chatSessionId] = useState<string>(
    () =>
      `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
  );
  // Whether to also upload inverse-kinematics files (in addition to the
  // standard metrics export) for the AI to analyze.
  const [includeInverseKinematics, setIncludeInverseKinematics] =
    useState(false);

  const steps = ["Player", "Select Session(s)", "Movement Type", "Report Type"];

  // AI Data Export mutation
  const [uploadAiData] = useUploadAiDataMutation();

  // Container warm-up — replaces the upload_data container_id with the
  // chat-ready one once initialization completes. Stream state is forwarded
  // to TestingPanel so re-runs can show live output. Failures are non-fatal:
  // the chat flow still works against the original container_id.
  const {
    initContainer,
    isStreaming: isInitStreaming,
    streamedContent: initStreamedContent,
    error: initError,
  } = useInitContainer({
    onComplete: (containerId, summary) => {
      if (!containerId && !summary) return;
      setUploadDataResponse((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(containerId ? { containerId } : {}),
          ...(summary ? { dataSummary: summary } : {}),
        };
      });
    },
  });

  // Get button text based on current step
  const getButtonText = () => {
    if (currentStep === 1) return "Continue";
    if (currentStep === 2) return "Next";
    if (currentStep === 3) return "Load Data";
    return "Continue";
  };

  // Get the selected player (single select - use first element of array)
  const selectedPlayer = dataset.players.length > 0 ? dataset.players[0] : "";

  const {
    sessions: playerSessions,
    isFetching: isFetchingSessions,
    isFetchingAll,
    error: sessionsError,
    allowRetry: sessionsAllowRetry,
    activePlayerId,
    startFetch,
    reset: resetSessions,
  } = usePlayerSessions();

  // Sync hook error/allowRetry to step 1 UI state
  useEffect(() => {
    if (currentStep === 1) {
      setStep1Error(sessionsError);
      setStep1ErrorAllowRetry(sessionsAllowRetry);
    }
  }, [currentStep, sessionsError, sessionsAllowRetry]);

  // Move to step 2 when all sessions are loaded (no error, has sessions, matches selected player)
  useEffect(() => {
    if (
      !isFetchingAll &&
      !isFetchingSessions &&
      currentStep === 1 &&
      !sessionsError &&
      playerSessions.length > 0 &&
      activePlayerId === selectedPlayer
    ) {
      setCurrentStep(2);
    }
  }, [
    isFetchingAll,
    isFetchingSessions,
    currentStep,
    sessionsError,
    playerSessions.length,
    activePlayerId,
    selectedPlayer,
  ]);

  // Cleanup loading interval on unmount
  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    };
  }, []);

  // Check step completion
  const isStep1Complete = selectedPlayer !== "";
  const isStep2Complete = dataset.sessions.length > 0;
  const isStep3Complete = dataset.movementTypeId !== "";
  const isStep4Complete = inputValue.trim() !== "";

  // Check if dataset is complete
  const isDatasetComplete =
    selectedPlayer !== "" && dataset.movementTypeId !== "";

  const handleSendOrInitiate = () => {
    if (isDatasetComplete && inputValue.trim()) {
      const containerId = uploadDataResponse?.containerId ?? "";
      const fileIds = uploadDataResponse?.fileIds ?? [];
      const dataSummary = uploadDataResponse?.dataSummary ?? "";

      // Call the parent handler with all necessary data
      onInitiateChatWithData(
        chatSessionId,
        containerId,
        inputValue,
        fileIds,
        dataSummary,
      );
    }
  };

  // Clear the simulated-progress interval if one is running. Called before
  // starting a run (guards against a leftover timer from a rapid retry) and
  // again on completion (to stop the simulation).
  const clearProgressInterval = () => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
  };

  // The upload_data API reports no incremental progress, so simulate it:
  // tick up by 5% every 200ms, capped at 90% until the call resolves and
  // snaps the bar to 100%.
  const startProgressInterval = () => {
    loadingIntervalRef.current = setInterval(() => {
      setLoadingProgress((prev) => (prev >= 90 ? prev : prev + 5));
    }, 200);
  };

  // Apply a successful upload_data response to UI state. Any failure (failed
  // uploads/sessions, or nothing succeeded) shows the error state; only a
  // clean success warms up the chat container.
  const applyUploadResult = (response: AiUploadDataResponse) => {
    setUploadDataResponse(response);
    const failedUploadsData = response?.failedUploads || [];
    const failed = response?.failedSessions || [];
    const succeeded = response?.succeededSessions || [];

    setFailedUploads(failedUploadsData);
    setFailedSessions(failed);
    setSucceededSessions(succeeded);

    setLoadingProgress(100);
    if (failedUploadsData.length > 0 || failed.length > 0) {
      setLoadingStatus("error");
    } else if (succeeded.length > 0) {
      setLoadingStatus("success");
      initContainer(response?.fileIds ?? []);
    } else {
      // No sessions succeeded or failed - treat as error
      setLoadingStatus("error");
    }

    clearProgressInterval();
  };

  // Kick off (or retry) the AI data export: reset loading state, fire the
  // mutation, and run the progress simulation until it resolves.
  const runUpload = () => {
    setLoadingStatus("loading");
    setLoadingProgress(0);
    setFailedUploads([]);
    setFailedSessions([]);
    setSucceededSessions([]);
    setUploadDataResponse(null);

    clearProgressInterval();

    const requestBody: AiUploadDataRequest = {
      org_player_ids: dataset.players,
      movement_type_id: Number(dataset.movementTypeId),
      session_ids: dataset.sessions,
      ...(includeInverseKinematics
        ? { data_types: ["inverse-kinematics"] }
        : {}),
    };

    uploadAiData(requestBody)
      .unwrap()
      .then(applyUploadResult)
      .catch(() => {
        setLoadingProgress(100);
        setLoadingStatus("error");
        setFailedUploads([]);
        setFailedSessions([]);
        setSucceededSessions([]);
        clearProgressInterval();
      });

    startProgressInterval();
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (selectedPlayer) {
        startFetch(selectedPlayer);
      }
    } else if (currentStep === 3) {
      // Step 3: Start loading data and move to step 4
      setCurrentStep(4);
      runUpload();
    } else if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      if (currentStep === 2) {
        resetSessions();
        setStep1Error(null);
        setStep1ErrorAllowRetry(true);
      }
      // Reset loading state if going back from step 4
      if (currentStep === 4) {
        if (loadingIntervalRef.current) {
          clearInterval(loadingIntervalRef.current);
          loadingIntervalRef.current = null;
        }
        setLoadingStatus(null);
        setLoadingProgress(0);
        setFailedUploads([]);
        setFailedSessions([]);
        setSucceededSessions([]);
        setUploadDataResponse(null);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedFromStep = () => {
    switch (currentStep) {
      case 1:
        return isStep1Complete;
      case 2:
        return isStep2Complete;
      case 3:
        return isStep3Complete;
      case 4:
        // For step 4, we need both the input and the dataset to be complete
        return isStep4Complete && isDatasetComplete;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PlayerSelectStep
            selectedPlayer={selectedPlayer}
            onPlayerChange={(playerId) => {
              setDataset({
                ...dataset,
                players: playerId ? [playerId] : [],
              });
              setStep1Error(null);
              setStep1ErrorAllowRetry(true);
              if (currentStep === 1) {
                resetSessions();
              }
            }}
            error={step1Error}
            allowRetry={step1ErrorAllowRetry}
            onRetry={() => {
              if (selectedPlayer) {
                setStep1Error(null);
                setStep1ErrorAllowRetry(true);
                startFetch(selectedPlayer);
              }
            }}
            isDisabled={isFetchingSessions}
          />
        );
      case 2:
        return (
          <SessionSelectStep
            selectedSessionIds={dataset.sessions}
            onSessionChange={(sessionIds) => {
              const firstSession = sessionIds[0]
                ? playerSessions.find((s) => s.id === sessionIds[0])
                : null;
              setDataset({
                ...dataset,
                sessions: sessionIds,
                selectedSessionDate: firstSession?.sessionDate,
              });
            }}
            sessions={playerSessions}
          />
        );
      case 3:
        return (
          <MovementTypeStep
            selectedMovementType={dataset.movementTypeId}
            onMovementTypeChange={(value) =>
              setDataset({ ...dataset, movementTypeId: value })
            }
            includeInverseKinematics={includeInverseKinematics}
            onIncludeInverseKinematicsChange={setIncludeInverseKinematics}
          />
        );
      case 4:
        return (
          <>
            <ReportTypeStep
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSend={handleSendOrInitiate}
              onVoiceTranscript={onVoiceTranscript}
              loadingStatus={loadingStatus}
              loadingProgress={loadingProgress}
              failedUploads={failedUploads}
              failedSessions={failedSessions}
              succeededSessions={succeededSessions}
              availableSessions={playerSessions}
              fileIds={uploadDataResponse?.fileIds ?? []}
              onRetry={runUpload}
            />
            {loadingStatus === "success" && uploadDataResponse && (
              <TestingPanel
                fileIds={uploadDataResponse.fileIds ?? []}
                filesList={uploadDataResponse.filesList}
                dataSummary={uploadDataResponse.dataSummary}
                containerId={uploadDataResponse.containerId}
                initContainer={initContainer}
                initStream={{
                  isStreaming: isInitStreaming,
                  streamedContent: initStreamedContent,
                  error: initError,
                }}
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 bg-[#0b0b0b] overflow-auto flex flex-col items-center pt-20 pb-6 gap-6">
      {/* Title */}
      <div className="box-border content-stretch flex flex-col gap-[16px] items-center not-italic pb-[30px] pt-0 px-0 relative shrink-0 text-center text-white">
        <div className="flex flex-col font-['Bebas_Neue'] font-normal justify-center relative shrink-0 text-[64px] uppercase w-[523px] leading-[36px]">
          <p data-testid="reboot-live-title" className="leading-[36px]">
            Reboot Live
          </p>
        </div>
        <p
          data-testid="reboot-live-description"
          className="font-normal leading-[23px] relative shrink-0 text-[16px] w-[523px] text-white"
        >
          AI-powered biomechanics reporting that translates movement into
          performance metrics.
        </p>
        {onOpenImportModal && (
          <div className="flex flex-col items-center gap-2">
            <ImportPreviousConversationButton onClick={onOpenImportModal} />
          </div>
        )}
      </div>

      {/* Stepper */}
      <RebootLiveStepper currentStep={currentStep} steps={steps} />

      {/* Step Content */}
      {renderStepContent()}

      {/* Navigation Buttons */}
      {currentStep <= 4 && (
        <div className="content-stretch flex gap-[10px] items-start relative shrink-0 w-full max-w-[960px]">
          <div className="basis-0 grow min-h-px min-w-px relative shrink-0">
            <div className="size-full">
              <div
                className={`box-border content-stretch flex items-center py-[24px] relative ${
                  currentStep === 1
                    ? "justify-center w-full"
                    : currentStep === 4
                      ? "justify-center w-[600px] mx-auto"
                      : "justify-between w-[600px] mx-auto"
                }`}
              >
                {currentStep > 1 && (
                  <button
                    data-testid="reboot-live-setup-back-button"
                    onClick={handleBack}
                    className="box-border content-stretch flex flex-col gap-[10px] h-[48px] items-center px-[32px] py-[14px] relative rounded-[6px] shrink-0 hover:opacity-80 transition-opacity"
                  >
                    <div
                      aria-hidden="true"
                      className="absolute border border-[#fb6a07] border-solid inset-0 pointer-events-none rounded-[6px]"
                    />
                    <p className="font-semibold leading-[20px] text-[16px] text-white">
                      Back
                    </p>
                  </button>
                )}
                {currentStep < 4 && (
                  <>
                    {currentStep === 1 && isFetchingSessions ? (
                      <SpinnerButton
                        btnText="Loading"
                        className="!w-auto bg-[#fb6a07] h-[48px] px-[32px] py-[14px] rounded-[6px] shrink-0"
                      />
                    ) : (
                      <button
                        data-testid="reboot-live-setup-continue-button"
                        onClick={handleNext}
                        disabled={!canProceedFromStep() || isFetchingSessions}
                        className="bg-[#fb6a07] box-border content-stretch flex flex-col h-[48px] items-center justify-center px-[32px] py-[14px] relative rounded-[6px] shrink-0 hover:opacity-90 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <p className="font-semibold leading-[20px] text-[16px] text-white">
                          {getButtonText()}
                        </p>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
